import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useFeishuTestNotify } from '@/hooks/useFeishuNotify';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Send, Loader2, CheckCircle, XCircle, MessageSquare, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';
import { zhCN } from 'date-fns/locale';

interface FeishuMessage {
  id: string;
  message_type: string;
  content: Record<string, unknown>;
  status: string;
  sent_at: string | null;
  response: Record<string, unknown> | null;
  created_at: string;
}

export function FeishuSettings() {
  const { mutate: sendTest, isPending: isTesting } = useFeishuTestNotify();
  
  const { data: messages, isLoading, refetch } = useQuery({
    queryKey: ['feishu-messages'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('feishu_messages')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(20);
      
      if (error) throw error;
      return data as FeishuMessage[];
    },
  });

  const handleTestMessage = () => {
    sendTest(undefined, {
      onSuccess: () => {
        setTimeout(() => refetch(), 1000);
      },
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'sent':
        return <Badge variant="default" className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" />已发送</Badge>;
      case 'failed':
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />失败</Badge>;
      default:
        return <Badge variant="secondary">待处理</Badge>;
    }
  };

  const getTypeBadge = (type: string) => {
    const typeConfig: Record<string, { label: string; className: string }> = {
      contact: { label: '咨询', className: 'bg-blue-500' },
      blog: { label: '博客', className: 'bg-purple-500' },
      project: { label: '项目', className: 'bg-green-500' },
      test: { label: '测试', className: 'bg-orange-500' },
    };
    const config = typeConfig[type] || { label: type, className: 'bg-gray-500' };
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">飞书通知设置</h2>
          <p className="text-muted-foreground">管理飞书机器人通知和查看消息历史</p>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              连接状态
            </CardTitle>
            <CardDescription>
              测试飞书机器人连接是否正常
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">
                  点击按钮发送测试消息到飞书群，验证 Webhook 配置是否正确。
                </p>
              </div>
            </div>
            <Button 
              onClick={handleTestMessage} 
              disabled={isTesting}
              className="w-full"
            >
              {isTesting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  发送中...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  发送测试消息
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>通知类型说明</CardTitle>
            <CardDescription>
              系统支持的飞书通知类型
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Badge className="bg-blue-500">咨询</Badge>
                <span className="text-sm text-muted-foreground">新咨询预约通知</span>
              </div>
              <div className="flex items-center gap-3">
                <Badge className="bg-purple-500">博客</Badge>
                <span className="text-sm text-muted-foreground">新博客发布通知</span>
              </div>
              <div className="flex items-center gap-3">
                <Badge className="bg-green-500">项目</Badge>
                <span className="text-sm text-muted-foreground">新项目建议通知</span>
              </div>
              <div className="flex items-center gap-3">
                <Badge className="bg-orange-500">测试</Badge>
                <span className="text-sm text-muted-foreground">连接测试消息</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>消息历史</CardTitle>
            <CardDescription>最近发送的飞书通知记录</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="w-4 h-4 mr-2" />
            刷新
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : messages && messages.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>类型</TableHead>
                  <TableHead>状态</TableHead>
                  <TableHead>发送时间</TableHead>
                  <TableHead>创建时间</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {messages.map((msg) => (
                  <TableRow key={msg.id}>
                    <TableCell>{getTypeBadge(msg.message_type)}</TableCell>
                    <TableCell>{getStatusBadge(msg.status)}</TableCell>
                    <TableCell>
                      {msg.sent_at 
                        ? format(new Date(msg.sent_at), 'yyyy-MM-dd HH:mm:ss', { locale: zhCN })
                        : '-'}
                    </TableCell>
                    <TableCell>
                      {format(new Date(msg.created_at), 'yyyy-MM-dd HH:mm:ss', { locale: zhCN })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              暂无消息记录
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
